import { ApplianceItemType, ApplianceType } from '@alpinehomeair/common';
import { ItemType, LevelType, SpaceType } from 'data/types';
import { nanoid } from 'nanoid';

export const FIREPLACE_APPLIANCE_CODE = 'fireplace';
export const FIREPLACE_APPLIANCE_ITEM_ID = -1;

export const FIREPLACE_APPLIANCE_ITEM: ApplianceItemType = {
  itemId: FIREPLACE_APPLIANCE_ITEM_ID,
  code: FIREPLACE_APPLIANCE_CODE,
  label: 'Fireplace',
  wattage: 0,
  inUseOutput: 0,
  perHourUsage: 0,
  BTUs: 0
};

const EXCLUDED_APPLIANCE_CODES = new Set([
  'toaster',
  'coffee_maker_brewer',
  'cooking_range_no_hood',
  'cooking_range_without_exhaust'
]);

const ALWAYS_INCLUDED_APPLIANCE_CODES = new Set([
  'cooking_range_with_exhaust',
  FIREPLACE_APPLIANCE_CODE
]);

export function getSignificantAppliances(
  applianceItems: ApplianceItemType[]
): ApplianceItemType[] {
  return applianceItems.filter(
    (item) =>
      !EXCLUDED_APPLIANCE_CODES.has(item.code) &&
      (ALWAYS_INCLUDED_APPLIANCE_CODES.has(item.code) ||
        item.wattage > 500 ||
        item.BTUs > 1500) &&
      !item.label.toLowerCase().includes('no hood')
  );
}

export function getAdvancedAppliances(
  applianceItems: ApplianceItemType[]
): ApplianceItemType[] {
  const significantItemIds = new Set(
    getSignificantAppliances(applianceItems).map((item) => item.itemId)
  );
  return applianceItems.filter((item) => !significantItemIds.has(item.itemId));
}

export function filterAppliancesBySearchText(
  applianceItems: ApplianceItemType[],
  searchText: string
): ApplianceItemType[] {
  return applianceItems.filter((item) =>
    item.label.toLowerCase().includes(searchText.toLowerCase())
  );
}

export function createFireplaceAppliance(): ApplianceType {
  return {
    uniqueId: nanoid(5),
    itemId: FIREPLACE_APPLIANCE_ITEM_ID,
    label: FIREPLACE_APPLIANCE_ITEM.label,
    wattage: 0,
    inUseOutput: 0,
    perHourUsage: 0,
    custom: false
  };
}

export function countFireplaceAppliances(structure: LevelType[]): number {
  return structure
    .flatMap((level) => level.sections)
    .flatMap((section) => section.spaces)
    .reduce(
      (count, space) =>
        count +
        (space.sizingAttributes?.appliances?.filter(
          (appliance) => appliance.itemId === FIREPLACE_APPLIANCE_ITEM_ID
        ).length ?? 0),
      0
    );
}

function getSpaceItemCode(
  space: SpaceType,
  items: ItemType[]
): string | undefined {
  return items.find((item) => item.id === space.itemId)?.code;
}

export function findFireplaceTargetSpaceId(
  structure: LevelType[],
  items: ItemType[]
): string | null {
  const spaces = structure.flatMap((level) =>
    level.sections.flatMap((section) => section.spaces)
  );
  if (spaces.length === 0) {
    return null;
  }
  const byCode = (code: string) =>
    spaces.find((space) => getSpaceItemCode(space, items) === code);
  const target =
    byCode('living_room') ??
    byCode('family_room') ??
    spaces.find((space) => space.conditioned !== false) ??
    spaces[0];
  return target.uniqueId;
}
